import {
  LayoutDashboard,
  UserCog,
  GraduationCap,
  Settings,
  CalendarClock,
  CalendarDays,
  FileCheck,
  CalendarCheck,
  ListChecks,
  MessageSquare,
  BookOpen,
  ClipboardList,
  Award,
  Bus,
  Building2,
  Video,
  PenTool,
  Wallet,
  BarChart3,
  Receipt,
} from "lucide-react";

import { NavItem } from "../types/common";
import { ROUTES } from "../router/routes";

export const navigationConfig: NavItem[] = [
  // ── Dashboard ────────────────────────────────────────────────────────────────
  {
    title: "Dashboard",
    path: ROUTES.DASHBOARD.ROOT,
    icon: LayoutDashboard,
    requiredPermission: "Permissions.Dashboards.Read",
  },

  // ── My Details (Teacher self summary — same board as admin employee view) ──
  {
    title: "My Details",
    path: ROUTES.EMPLOYEE.MY_DETAILS,
    icon: UserCog,
    roles: ["Teacher"],
  },

  // ── Administration ────────────────────────────────────────────────────────
  {
    title: "Administration",
    icon: Settings,
    path: "/administration",
    requiredPermission: "Permissions.InstitutePreferences.Read",
    children: [
      {
        title: "Settings",
        path: ROUTES.SETTINGS.ROOT,
        requiredPermission: "Permissions.InstitutePreferences.Read",
      },
      {
        title: "Academic Years",
        path: ROUTES.SETTINGS.ACADEMIC_YEARS.LIST,
        requiredPermission: "Permissions.AcademicYears.Read",
      },
      {
        title: "Grading Systems",
        path: ROUTES.ACADEMIC.GRADING.LIST,
        requiredPermission: "Permissions.GradingSystem.Read",
      },
      {
        title: "Classes",
        path: ROUTES.ACADEMIC.CLASSES.LIST,
        requiredPermission: "Permissions.Classes.Read",
      },
      {
        title: "Subjects",
        path: ROUTES.ACADEMIC.SUBJECTS.LIST,
        requiredPermission: "Permissions.Subjects.Read",
      },
      {
        title: "Fee Structure",
        path: "/fee-structure",
        requiredPermission: "Permissions.FeeStructure.Read",
        featureFlag: "fees",
        children: [
          {
            title: "Fee Particulars",
            path: ROUTES.FINANCE.FEE.STRUCTURE,
            requiredPermission: "Permissions.FeeStructure.Read",
            featureFlag: "fees",
          },
          {
            title: "Collect Fees",
            path: ROUTES.FINANCE.FEE.COLLECT,
            requiredPermission: "Permissions.FeeStructure.Collect",
            featureFlag: "fees",
          },
        ],
      },
      {
        title: "Events",
        path: ROUTES.ACADEMIC.EVENTS.LIST,
        requiredPermission: "Permissions.Events.Read",
      },
      {
        title: "Holiday Management",
        path: ROUTES.CALENDAR.HOLIDAYS,
        requiredPermission: "Permissions.Holidays.Read",
      },
      {
        title: "Leave Policy Management",
        path: ROUTES.LEAVE.EMPLOYEE_LEAVE_POLICY,
        requiredPermission: "Permissions.Leaves.Policy.Manage",
      },
      {
        title: "Roles & Permissions",
        path: "/user-management",
        requiredPermission: "Permissions.UserManagement.Read",
        children: [
          {
            title: "Users",
            path: ROUTES.USER_MANAGEMENT.USERS,
            requiredPermission: "Permissions.UserManagement.Read",
          },
          {
            title: "Roles",
            path: ROUTES.USER_MANAGEMENT.ROLES,
            requiredPermission: "Permissions.Roles.Read",
          },
        ],
      },
    ],
  },

  // ── Students ──────────────────────────────────────────────────────────────
  {
    title: "Students",
    icon: GraduationCap,
    path: "/students",
    requiredPermission: "Permissions.Students.Read",
    children: [
      {
        title: "All Students",
        path: ROUTES.STUDENT.LIST,
        requiredPermission: "Permissions.Students.Read",
      },
      {
        title: "Add Student",
        path: ROUTES.STUDENT.NEW,
        requiredPermission: "Permissions.Students.Create",
      },
      {
        title: "Class Promotion",
        path: ROUTES.PROMOTION.EXECUTE,
        requiredPermission: "Permissions.Students.Update",
      },
      {
        title: "Section Assignment",
        path: ROUTES.PROMOTION.SECTION_ASSIGNMENT,
        requiredPermission: "Permissions.Students.Update",
      },
      {
        title: "Section Transfer",
        path: ROUTES.PROMOTION.SECTION_TRANSFER,
        requiredPermission: "Permissions.Students.Update",
      },
      {
        title: "Generate Certificate",
        path: ROUTES.CERTIFICATES.GENERATE,
        requiredPermission: "Permissions.Students.Read",
      },
      {
        title: "Pending Certificate Overrides",
        path: ROUTES.CERTIFICATES.TRANSFER_OVERRIDES,
        requiredPermission: "Permissions.TransferCertificateClearance.ApproveOverride",
      },
    ],
  },

  // ── Employees ────────────────────────────────────────────────────────────
  {
    title: "Employees",
    icon: UserCog,
    path: "/employees",
    requiredPermission: "Permissions.Employees.Read",
    children: [
      {
        title: "All Employees",
        path: ROUTES.STAFF.EMPLOYEES.LIST,
        requiredPermission: "Permissions.Employees.Read",
      },
      {
        title: "Role-wise Directory",
        path: ROUTES.STAFF.EMPLOYEES.DIRECTORY,
        requiredPermission: "Permissions.Employees.Read",
      },
      {
        title: "Add Employee",
        path: ROUTES.STAFF.EMPLOYEES.NEW,
        requiredPermission: "Permissions.Employees.Create",
      },
    ],
  },

  // ── Attendance ───────────────────────────────────────────────────────────
  {
    title: "Attendance",
    icon: ListChecks,
    path: "/attendance",
    requiredPermission: "Permissions.Attendance.Manage",
    children: [
      {
        title: "Student Attendance",
        path: ROUTES.ACADEMIC.ATTENDANCE.STUDENT,
        requiredPermission: "Permissions.Attendance.ManageStudent",
      },
      {
        title: "Employee Attendance",
        path: ROUTES.ACADEMIC.ATTENDANCE.EMPLOYEE,
        requiredPermission: "Permissions.Attendance.ManageEmployee",
      },
    ],
  },

  // ── Exams (admin / teacher view) ─────────────────────────────────────────
  {
    title: "Exams",
    icon: FileCheck,
    path: "/examination-management",
    requiredPermission: "Permissions.Exam.Manage",
    children: [
      {
        title: "View Exams",
        path: ROUTES.EXAM.LIST,
        // requiredPermission: "Permissions.Exam.Read",
      },
      {
        title: "Enter Scores",
        path: ROUTES.EXAM.ENTER_SCORES,
        requiredPermission: "Permissions.Exam.Update",
      },
      {
        title: "Create Exam",
        path: ROUTES.EXAM.CREATE,
        requiredRoleType: "admin",
      },
    ],
  },

  // ── Exams (teacher-only shortcut) ────────────────────────────────────────
  {
    title: "My Exams",
    icon: FileCheck,
    path: ROUTES.EXAM.MY_EXAMS,
    requiredPermission: "Permissions.Exam.Read",
    roles: ["Teacher"],
  },
  {
    title: "To-Do",
    icon: ListChecks,
    path: ROUTES.EXAM.TODOS,
    requiredPermission: "Permissions.Exam.Read",
    roles: ["Teacher"],
  },
  {
    title: "Enter Scores",
    icon: Award,
    path: ROUTES.EXAM.ENTER_SCORES,
    requiredPermission: "Permissions.Exam.Update",
    roles: ["Teacher"],
  },
  {
    title: "Create Exam",
    icon: FileCheck,
    path: ROUTES.EXAM.CREATE,
    requiredPermission: "Permissions.Exam.Create",
    roles: ["Teacher"],
  },

  // ── Exams (student view) ─────────────────────────────────────────────────
  {
    title: "Exam Management",
    icon: FileCheck,
    roles: ["Student"],
    path: "/exams",
    children: [
      {
        title: "My Exams",
        path: ROUTES.EXAM.STUDENT_SCHEDULES,
        roles: ["Student"],
      },
      {
        title: "Exam Results",
        path: ROUTES.EXAM.RESULTS,
        roles: ["Student"],
      },
    ],
  },

  // ── Syllabus (admin / teacher) ────────────────────────────────────────────
  {
    title: "Syllabus",
    icon: BookOpen,
    path: ROUTES.SYLLABUS.LIST,
    requiredPermission: "Permissions.Syllabus.Read",
  },

  // ── Assignments (admin / teacher) ─────────────────────────────────────────
  {
    title: "Assignments",
    icon: ClipboardList,
    path: ROUTES.ASSIGNMENT.LIST,
    requiredPermission: "Permissions.Assignments.Read",
  },

  // ── Homework (Admin / Teacher / Student) ──────────────────────────────────
  {
    title: "Homework",
    icon: PenTool,
    path: ROUTES.HOMEWORK.ROOT,
    requiredPermission: "Permissions.Homework.Read",
  },

  // ── Online Classes (teacher) ──────────────────────────────────────────────
  {
    title: "Online Classes",
    icon: Video,
    path: ROUTES.ONLINE_CLASSES.TEACHER.ROOT,
    requiredPermission: "Permissions.OnlineClasses.Read",
    roles: ["Teacher"],
    children: [
      {
        title: "Dashboard",
        path: ROUTES.ONLINE_CLASSES.TEACHER.DASHBOARD,
        requiredPermission: "Permissions.OnlineClasses.Read",
        roles: ["Teacher"],
      },
      {
        title: "Schedule",
        path: ROUTES.ONLINE_CLASSES.TEACHER.SCHEDULE,
        requiredPermission: "Permissions.OnlineClasses.Read",
        roles: ["Teacher"],
      },
      {
        title: "Recordings",
        path: ROUTES.ONLINE_CLASSES.TEACHER.RECORDINGS,
        requiredPermission: "Permissions.OnlineClasses.Read",
        roles: ["Teacher"],
      },
    ],
  },

  // ── Online Classes (student) ──────────────────────────────────────────────
  {
    title: "Online Classes",
    icon: Video,
    requiredPermission: "Permissions.OnlineClasses.Read",
    roles: ["Student"],
    path: ROUTES.ONLINE_CLASSES.STUDENT.ROOT,
    children: [
      {
        title: "Dashboard",
        path: ROUTES.ONLINE_CLASSES.STUDENT.DASHBOARD,
        requiredPermission: "Permissions.OnlineClasses.Read",
        roles: ["Student"],
      },
      {
        title: "Recordings",
        path: ROUTES.ONLINE_CLASSES.STUDENT.RECORDINGS,
        requiredPermission: "Permissions.OnlineClasses.Read",
        roles: ["Student"],
      },
    ],
  },

  // ── My Fees (student-only) ────────────────────────────────────────────────
  {
    title: "My Fees",
    icon: Wallet,
    path: ROUTES.FINANCE.FEE.MY_FEES,
    roles: ["Student"],
  },

  // ── Events (teacher shortcut — admins reach it via Administration) ────────
  {
    title: "Events",
    icon: CalendarDays,
    path: ROUTES.ACADEMIC.EVENTS.LIST,
    requiredPermission: "Permissions.Events.Read",
    roles: ["Teacher"],
  },

  // ── My Events (student-only) ──────────────────────────────────────────────
  {
    title: "My Events",
    icon: CalendarDays,
    path: ROUTES.ACADEMIC.EVENTS.MY_EVENTS,
    roles: ["Student"],
  },

  // ── Timetable ─────────────────────────────────────────────────────────────
  {
    title: "Timetable",
    icon: CalendarClock,
    path: "/timetable",
    requiredPermission: "Permissions.TimeTable.Read",
    children: [
      {
        title: "Manage Timetable Slots",
        path: ROUTES.SCHEDULE.TIMETABLE.SCHEDULE_PROFILES,
        requiredPermission: "Permissions.TimeTable.Manage",
        requiredRoleType: "admin",
      },
      {
        title: "Schedule Timetable",
        path: ROUTES.SCHEDULE.TIMETABLE.DASHBOARD,
        requiredPermission: "Permissions.TimeTable.Read",
        requiredRoleType: "admin",
      },
      {
        title: "View Schedule",
        path: ROUTES.SCHEDULE.TIMETABLE.SCHEDULE_VIEWER,
        requiredPermission: "Permissions.TimeTable.Read",
        requiredRoleType: "admin",
      },
      {
        title: "My Timetable",
        path: ROUTES.SCHEDULE.TIMETABLE.MY_TIMETABLE,
        requiredPermission: "Permissions.TimeTable.Read",
        roles: ["Student"],
      },
      {
        title: "My Schedule",
        path: ROUTES.SCHEDULE.TIMETABLE.MY_SCHEDULE,
        requiredPermission: "Permissions.TimeTable.Read",
        roles: ["Teacher"],
      },
      {
        title: "My Availability",
        path: ROUTES.SCHEDULE.TIMETABLE.MY_AVAILABILITY,
        requiredPermission: "Permissions.TimeTable.Read",
        roles: ["Teacher"],
      },
    ],
  },

  // ── Leaves & Holidays ────────────────────────────────────────────────────
  {
    title: "Leaves & Holidays",
    icon: CalendarCheck,
    path: "/leaves",
    requiredPermission: "Permissions.Leaves.Read",
    children: [
      {
        title: "Holidays",
        path: ROUTES.CALENDAR.HOLIDAYS,
        requiredPermission: "Permissions.Holidays.Read",
      },
      {
        title: "Pending Approvals",
        path: ROUTES.APPROVALS.PENDING,
        requiredPermission: "Permissions.Leaves.Approve",
      },
      {
        title: "My Students' Leave Requests",
        path: ROUTES.APPROVALS.STUDENTS,
        requiredPermission: "Permissions.Leaves.ApproveStudents",
      },
      {
        title: "My Leaves",
        path: ROUTES.LEAVE.LIST,
        requiredPermission: "Permissions.Leaves.ViewOwn",
      },
      {
        title: "Apply Leave",
        path: ROUTES.LEAVE.APPLY,
        requiredPermission: "Permissions.Leaves.Apply",
      },
    ],
  },
  {
    title: "Transport",
    path: "/transport",
    icon: Bus,
    requiredPermission: "Permissions.TransportManagement.Read",
    children: [
      {
        title: "Vehicles",
        path: ROUTES.TRANSPORT.VEHICLES.LIST,
        requiredPermission: "Permissions.Vehicles.Read",
      },
      {
        title: "Routes",
        path: ROUTES.TRANSPORT.ROUTES.LIST,
        requiredPermission: "Permissions.Routes.Read",
      },
      {
        title: "Enrollment",
        path: ROUTES.TRANSPORT.ENROLLMENT.LIST,
        requiredPermission: "Permissions.TransportManagement.Enrollment",
      },
      {
        title: "Attendance",
        path: ROUTES.TRANSPORT.ATTENDANCE.LIST,
        requiredPermission: "Permissions.TransportManagement.Attendance",
      },
      {
        title: "Transport Logs",
        path: ROUTES.TRANSPORT.LOGS,
        requiredPermission: "Permissions.TransportManagement.Attendance",
      },
    ],
  },
  {
    title: "Hostels",
    path: "/hostel",
    icon: Building2,
    requiredPermission: "Permissions.HostelManagement.Read",
    children: [
      {
        title: "Hostels",
        path: ROUTES.HOSTEL.LIST,
        requiredPermission: "Permissions.HostelManagement.Read",
      },
      {
        title: "Residents",
        path: ROUTES.HOSTEL.RESIDENTS.LIST,
        requiredPermission: "Permissions.HostelManagement.Read",
      },
      {
        title: "Attendance",
        path: ROUTES.HOSTEL.ATTENDANCE.LIST,
        requiredPermission: "Permissions.HostelManagement.Attendance",
      },
    ],
  },

  // ── Communication ─────────────────────────────────────────────────────────
  {
    title: "Communication",
    icon: MessageSquare,
    path: ROUTES.COMMUNICATION.DASHBOARD,
    // requiredPermission: "Permissions.Communication.Chat",
    children: [
      {
        title: "Dashboard",
        path: ROUTES.COMMUNICATION.DASHBOARD,
        requiredPermission: "Permissions.Communication.Dashboard",
      },
      {
        title: "Fee Reminders",
        path: ROUTES.COMMUNICATION.FEE_REMINDERS,
        requiredPermission: "Permissions.Communication.FeeReminders",
        featureFlag: "fees",
      },
      {
        title: "Messages",
        path: ROUTES.COMMUNICATION.MESSAGES,
        requiredPermission: "Permissions.Communication.Chat",
      },
    ],
  },

  // ── Reports ───────────────────────────────────────────────────────────────
  {
    title: "Reports",
    icon: BarChart3,
    path: ROUTES.REPORTS.EXAM_SCORES,
    requiredPermission: "Permissions.Reports.Read",
  },
  // ── Expenses ──────────────────────────────────────────────────────────────
  {
    title: "Expenses",
    icon: Receipt,
    path: "/expenses",
    // No parent permission — sidebar auto-hides if no children are accessible
    children: [
      {
        title: "Expense Categories",
        path: ROUTES.EXPENSES.CATEGORIES.LIST,
        requiredPermission: "Permissions.Expenses.ManageCategories",
      },
      {
        title: "My Claims",
        path: ROUTES.EXPENSES.CLAIMS.MY,
        requiredPermission: "Permissions.Expenses.Create",
        requiredRoleType: "employee",
      },
      {
        title: "Pending Approvals",
        path: ROUTES.EXPENSES.APPROVALS.PENDING,
        hideForEmployeeWhenNoPendingApprovals: true,
      },
      {
        title: "Settlements",
        path: ROUTES.EXPENSES.SETTLEMENTS.LIST,
        requiredPermission: "Permissions.Expenses.Settle",
      },
    ],
  },

];
